import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ThumbsUp, Eye, Send, Share2, Flag, Pin, PinOff, Check } from "lucide-react";
import { api } from "../api";
import { useAuth, useGuard } from "../auth";
import { subscribe, unsubscribe, onEvent } from "../socket";
import { recordSubscription } from "../subscriptions";
import { fmtNum, timeAgo } from "../config";
import Player from "../components/Player";
import { SupportButton } from "../components/Payments";
import { Avatar, VideoCard, Spinner } from "../components/cards";

const Watch = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const guard = useGuard();
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [next, setNext] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);
  const [text, setText] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [shared, setShared] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    setVideo(null);
    setReported(false);
    setShared(false);
    api.get(`/videos/${id}`).then((v) => {
      setVideo(v);
      setLiked(!!v.liked);
      setLikes(v.likes ?? 0);
      setViews(v.views ?? 0);
      setSubscribed(!!(v.creator?.isSubscribed || v.isSubscribed));
      setPinned(!!(v.creator?.pinnedVideoIds || []).includes(v.id));
    }).catch(() => {});
    api.post(`/videos/${id}/view`).catch(() => {});
    api.get(`/videos/${id}/comments`).then((r) => setComments(Array.isArray(r) ? r : r.comments || [])).catch(() => {});
    api.get(`/videos/${id}/next`).then((r) => setNext(Array.isArray(r) ? r : r.videos || [])).catch(() => {});

    subscribe(`video:${id}`);
    const offComment = onEvent("comment", (ev) => {
      if (ev.comment) setComments((c) => [ev.comment, ...c]);
    });
    const offLikes = onEvent("likes", (ev) => {
      if (ev.likes != null) setLikes(ev.likes);
    });
    const offViews = onEvent("views", (ev) => {
      if (ev.views != null) setViews(ev.views);
    });
    return () => {
      unsubscribe(`video:${id}`);
      offComment();
      offLikes();
      offViews();
    };
  }, [id]);

  if (!video) return <Spinner />;

  const isOwn = user && user.id === video.creatorId;

  const toggleLike = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/videos/${id}/like`);
      setLiked(r.liked ?? !liked);
      if (r.likes != null) setLikes(r.likes);
      else setLikes((n) => (r.liked ?? !liked) ? n + 1 : Math.max(0, n - 1));
    } catch {}
  };

  const toggleSubscribe = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/creators/${video.creatorId}/subscribe`);
      const now = r.subscribed ?? !subscribed;
      setSubscribed(now);
      recordSubscription(video.creatorId, now);
    } catch {}
  };

  const share = async () => {
    if (!guard()) return;
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: video.title, url });
      else await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {}
  };

  const report = async () => {
    if (!guard() || reported) return;
    try {
      await api.post("/reports", { kind: "video", targetId: id, ref: id, reason: "Reported from the web app" });
    } catch {}
    setReported(true);
  };

  const togglePin = async () => {
    if (!guard()) return;
    try {
      await api.post(`/videos/${id}/${pinned ? "unpin" : "pin"}`);
      setPinned(!pinned);
    } catch {}
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (!guard()) return;
    if (!text.trim()) return;
    try {
      const c = await api.post(`/videos/${id}/comments`, { text: text.trim() });
      setComments((cs) => [c, ...cs]);
      setText("");
    } catch {}
  };

  const actionBtn = (active) =>
    `flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
      active ? "border-crimson/50 bg-crimson/15 text-crimson" : "border-white/10 text-slate-300 hover:border-white/25"
    }`;

  return (
    <div className="mx-auto grid max-w-7xl gap-8 xl:grid-cols-[1fr_360px]" data-testid="watch-page">
      <div>
        <Player video={video} className="aspect-video w-full rounded-2xl" />
        <h1 className="mt-5 font-heading text-xl font-bold tracking-tight text-white sm:text-2xl" data-testid="watch-title">{video.title}</h1>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to={`/app/creator/${video.creatorId}`}>
              <Avatar user={video.creator} size={42} />
            </Link>
            <div>
              <Link to={`/app/creator/${video.creatorId}`} className="font-heading text-sm font-semibold text-white hover:text-crimson" data-testid="watch-creator-link">
                {video.creator?.displayName || video.creator?.username}
              </Link>
              <p className="text-xs text-slate-500">{fmtNum(video.creator?.subscribers)} subscribers</p>
            </div>
            {!isOwn && (
              <button onClick={toggleSubscribe} data-testid="subscribe-button" className={`ml-2 rounded-full px-5 py-2 font-heading text-xs font-bold transition-colors ${subscribed ? "border border-white/15 text-slate-300" : "bg-crimson text-white hover:bg-crimson-hover"}`}>
                {subscribed ? "Subscribed" : "Subscribe"}
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={toggleLike} data-testid="like-button" className={actionBtn(liked)}>
              <ThumbsUp size={15} fill={liked ? "currentColor" : "none"} /> {fmtNum(likes)}
            </button>
            <button onClick={share} data-testid="share-button" className={actionBtn(shared)}>
              {shared ? <Check size={15} /> : <Share2 size={15} />} {shared ? "Link copied" : "Share"}
            </button>
            {isOwn && (
              <button onClick={togglePin} data-testid="pin-button" className={actionBtn(pinned)}>
                {pinned ? <PinOff size={15} /> : <Pin size={15} />} {pinned ? "Unpin" : "Pin"}
              </button>
            )}
            <button onClick={report} data-testid="report-button" className={actionBtn(reported)}>
              <Flag size={15} /> {reported ? "Reported" : "Report"}
            </button>
            <SupportButton target={{ id: video.id, title: video.title }} creator={video.creator} />
            <span className="flex items-center gap-2 text-sm text-slate-500" data-testid="watch-views">
              <Eye size={15} /> {fmtNum(views)}
            </span>
          </div>
        </div>
        {video.description && (
          <p className="mt-5 whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm leading-relaxed text-slate-400" data-testid="watch-description">
            {video.description}
          </p>
        )}

        <section className="mt-8" data-testid="comments-section">
          <h2 className="mb-4 font-heading text-base font-bold text-white">{comments.length} Comments</h2>
          <form onSubmit={sendComment} className="mb-6 flex gap-3">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onFocus={() => !user && guard()}
              placeholder={user ? "Add a comment…" : "Sign in to comment…"}
              data-testid="comment-input"
              className="flex-1 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
            />
            <button className="rounded-full bg-crimson p-2.5 text-white transition-colors hover:bg-crimson-hover" data-testid="comment-submit-button">
              <Send size={16} />
            </button>
          </form>
          <div className="space-y-5">
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
                <Avatar user={c.user || c.author} size={32} />
                <div>
                  <p className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-300">{(c.user || c.author)?.displayName || (c.user || c.author)?.username || "Viewer"}</span> · {timeAgo(c.createdAt)}
                  </p>
                  <p className="mt-1 text-sm text-slate-300">{c.text || c.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <aside>
        <h2 className="mb-4 font-heading text-base font-bold text-white">Up next</h2>
        <div className="space-y-4">
          {next.map((v) => <VideoCard key={v.id} video={v} />)}
          {next.length === 0 && <p className="text-sm text-slate-600">Nothing else yet.</p>}
        </div>
      </aside>
    </div>
  );
};

export default Watch;
